import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseChart } from './BaseChart.js';
import { formatCurrency, formatNumber } from '../../utils/dataFormatters.js';
import { generateChartAriaLabel, generateChartDescription } from '../../utils/accessibility.js';
import type { TimeSeriesChartProps } from '../../types/analytics.types.js';

/**
 * Production-grade time-series chart with proper axes, tooltips, legends, interactions, and accessibility
 * Currently uses CSS-based visualization, ready for chart library integration
 */
export const TimeSeriesChart = memo<TimeSeriesChartProps>(
  ({
    data,
    showRevenue = true,
    showOrders = true,
    showRefunds = false,
    isLoading,
    error,
    emptyMessage,
    emptyHint,
    title,
    subtitle,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  }) => {
    const { t } = useTranslation('admin');
    const resolvedTitle = title ?? t('analytics.charts.timeSeriesTitle');
    const resolvedSubtitle = subtitle ?? t('analytics.charts.timeSeriesSubtitle');

    const maxRevenue = useMemo(() => {
      if (data.length === 0) {
        return 0;
      }
      return Math.max(...data.map((d) => d.revenue), 0);
    }, [data]);

    const maxOrders = useMemo(() => {
      if (data.length === 0) {
        return 0;
      }
      return Math.max(...data.map((d) => d.orders), 0);
    }, [data]);

    const hasData = useMemo(() => {
      return data.length > 0 && (maxRevenue > 0 || maxOrders > 0);
    }, [data.length, maxRevenue, maxOrders]);

    const chartAriaLabel = useMemo(
      () => ariaLabel || generateChartAriaLabel(resolvedTitle, data.length, resolvedSubtitle),
      [ariaLabel, resolvedTitle, data.length, resolvedSubtitle]
    );

    const chartDescription = useMemo(
      () => ariaDescribedBy || generateChartDescription(t('analytics.charts.timeSeriesType'), data.length),
      [ariaDescribedBy, data.length, t]
    );

    return (
      <BaseChart
        isLoading={isLoading}
        error={error}
        emptyMessage={emptyMessage}
        emptyHint={emptyHint}
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        aria-label={chartAriaLabel}
        aria-describedby={chartDescription}
      >
        {hasData ? (
          <div className="flex flex-col gap-5 max-md:gap-3" role="img" aria-label={chartAriaLabel}>
            <div className="flex gap-5 flex-wrap pb-3 border-b border-[var(--color-an-border)] max-md:flex-col max-md:gap-2" role="list">
              {showRevenue ? (
                <div className="flex items-center gap-2 text-[13px] text-[var(--color-an-text-muted)] font-semibold" role="listitem">
                  <span
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ background: 'var(--color-an-chart-1)' }}
                  />
                  <span>{t('analytics.revenueMetricLabel')}</span>
                </div>
              ) : null}
              {showOrders ? (
                <div className="flex items-center gap-2 text-[13px] text-[var(--color-an-text-muted)] font-semibold" role="listitem">
                  <span
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ background: 'var(--color-an-chart-2)' }}
                  />
                  <span>{t('analytics.orders')}</span>
                </div>
              ) : null}
              {showRefunds ? (
                <div className="flex items-center gap-2 text-[13px] text-[var(--color-an-text-muted)] font-semibold" role="listitem">
                  <span
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ background: 'var(--color-an-chart-3)' }}
                  />
                  <span>{t('analytics.charts.refunds')}</span>
                </div>
              ) : null}
            </div>

            <div className="min-h-[300px] relative max-md:min-h-[200px] max-[480px]:min-h-[150px]">
              <div className="flex items-end gap-2 h-[300px] px-2 max-md:h-[200px] max-md:gap-1 max-[480px]:h-[150px]">
                {data.map((point) => (
                  <div
                    key={point.label}
                    className="flex-1 flex flex-col items-center gap-2 cursor-pointer transition-transform min-w-0 h-full hover:-translate-y-1 focus:outline focus:outline-2 focus:outline-[var(--color-an-primary)] focus:outline-offset-1 rounded max-md:min-w-11"
                    role="group"
                    aria-label={t('analytics.charts.timeSeriesPointAria', {
                      label: point.label,
                      revenue: formatCurrency(point.revenue),
                      orders: formatNumber(point.orders),
                    })}
                  >
                    <div className="w-full h-full flex items-end gap-0.5 relative">
                      {showRevenue && maxRevenue > 0 ? (
                        <div
                          className="flex-1 min-w-0 rounded-t self-end transition-opacity hover:opacity-85"
                          style={{
                            height: point.revenue > 0
                              ? `${(point.revenue / maxRevenue) * 100}%`
                              : '0%',
                            minHeight: point.revenue > 0 ? '2px' : '0',
                            background: 'var(--color-an-chart-1)',
                          }}
                          aria-label={t('analytics.charts.revenueAria', {
                            value: formatCurrency(point.revenue),
                          })}
                          title={t('analytics.charts.revenueAria', {
                            value: formatCurrency(point.revenue),
                          })}
                        />
                      ) : null}
                      {showOrders && maxOrders > 0 ? (
                        <div
                          className="flex-1 min-w-0 rounded-t self-end transition-opacity hover:opacity-85"
                          style={{
                            height: point.orders > 0
                              ? `${(point.orders / maxOrders) * 100}%`
                              : '0%',
                            minHeight: point.orders > 0 ? '2px' : '0',
                            background: 'var(--color-an-chart-2)',
                          }}
                          aria-label={t('analytics.charts.ordersAria', {
                            value: formatNumber(point.orders),
                          })}
                          title={t('analytics.charts.ordersAria', {
                            value: formatNumber(point.orders),
                          })}
                        />
                      ) : null}
                      {showRefunds && maxOrders > 0 ? (
                        <div
                          className="flex-1 min-w-0 rounded-t self-end transition-opacity hover:opacity-85"
                          style={{
                            height: point.refunds > 0
                              ? `${(point.refunds / maxOrders) * 100}%`
                              : '0%',
                            minHeight: point.refunds > 0 ? '2px' : '0',
                            background: 'var(--color-an-chart-3)',
                          }}
                          aria-label={t('analytics.charts.refundsAria', {
                            value: formatNumber(point.refunds),
                          })}
                          title={t('analytics.charts.refundsAria', {
                            value: formatNumber(point.refunds),
                          })}
                        />
                      ) : null}
                    </div>
                    <div className="text-[11px] text-[var(--color-an-text-muted)] text-center whitespace-nowrap overflow-hidden text-ellipsis w-full font-medium max-md:text-[10px]">{point.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </BaseChart>
    );
  }
);

TimeSeriesChart.displayName = 'TimeSeriesChart';
